import React from 'react';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend,
  BarElement,
  Filler,
} from 'chart.js';
import { Line, Bar } from 'react-chartjs-2';
import { RentalTrendData } from '../../services/dashboardService';

// BUG-S141-CHARTFILL: dataset ตั้ง `fill: type === 'line'` แต่ไม่ได้ register Filler
// → Chart.js เตือน "Tried to use the 'fill' option without the 'Filler' plugin enabled"
// แล้วทิ้งพื้นที่ใต้เส้นกราฟไปเงียบๆ (กราฟแนวโน้มบนแดชบอร์ดจึงเป็นเส้นเปล่า ไม่มีพื้นที่ระบายสี
// ต่างจากกราฟในหน้ารายงานที่ register Filler ไว้ครบ)
ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  BarElement,
  Title,
  Tooltip,
  Legend,
  Filler
);

interface RentalTrendChartProps {
  data: RentalTrendData[];
  type?: 'line' | 'bar';
  showRevenue?: boolean;
  isLoading?: boolean;
}

const RentalTrendChart: React.FC<RentalTrendChartProps> = ({ 
  data, 
  type = 'line', 
  showRevenue = false 
}) => {
  const options = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        position: 'top' as const,
      },
      title: {
        display: false,
      },
    },
    scales: {
      y: {
        beginAtZero: true,
        grid: {
          color: '#f3f4f6',
        },
        ticks: {
          color: '#6b7280',
          // KD3 S2464 (NEG-DASH-018): จำนวนการเช่าเป็นจำนวนเต็ม — เดือนที่ค่าน้อย/เป็น 0 ทั้งหมด
          // Chart.js เดิมออก tick 0.2, 0.4 … (สัญญา 0.2 ฉบับ)
          precision: 0,
        },
      },
      x: {
        grid: {
          color: '#f3f4f6',
        },
        ticks: {
          color: '#6b7280',
        },
      },
      ...(showRevenue && {
        y1: {
          type: 'linear' as const,
          display: true,
          position: 'right' as const,
          grid: {
            drawOnChartArea: false,
          },
          ticks: {
            color: '#6b7280',
            callback: function(value: any) {
              return '฿' + value.toLocaleString();
            }
          },
        },
      }),
    },
  };

  // ตรวจสอบว่า data เป็น array
  const dataArray = Array.isArray(data) ? data : [];
  
  const chartData = {
    labels: dataArray.map(item => item.month_name),
    datasets: [
      {
        label: 'จำนวนการเช่า',
        data: dataArray.map(item => item.rental_count),
        borderColor: 'rgb(59, 130, 246)',
        backgroundColor: 'rgba(59, 130, 246, 0.1)',
        fill: type === 'line',
        tension: 0.4,
        // monotone = โค้งไม่พุ่งเลย/ลงต่ำกว่าจุดข้อมูล (tension อย่างเดียว เดือนที่กระโดดจาก ~0 ขึ้นสูง
        // เส้นจะจุ่มต่ำกว่า 0 ก่อนขึ้น เหมือนมีจำนวนติดลบ)
        cubicInterpolationMode: 'monotone' as const,
      },
      ...(showRevenue ? [{
        label: 'รายได้ (บาท)',
        data: dataArray.map(item => item.revenue),
        borderColor: 'rgb(16, 185, 129)',
        backgroundColor: 'rgba(16, 185, 129, 0.1)',
        fill: false,
        yAxisID: 'y1',
        tension: 0.4,
        cubicInterpolationMode: 'monotone' as const,
      }] : []),
    ],
  };

  const ChartComponent = type === 'bar' ? Bar : Line;

  // แสดงข้อความเมื่อไม่มีข้อมูล
  if (dataArray.length === 0) {
    return (
      <div className="h-64 flex items-center justify-center text-gray-500">
        <p>ไม่มีข้อมูลแนวโน้มการเช่า</p>
      </div>
    );
  }

  return (
    <div className="h-64" role="region" aria-label="กราฟแนวโน้มการเช่า">
      <ChartComponent data={chartData} options={options} aria-label="กราฟแนวโน้มการเช่ารายเดือน" />
    </div>
  );
};

export default RentalTrendChart;